import React, { useState } from 'react';
import { supabase } from '../../../lib/supabase';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Icon from '../../../components/AppIcon';

const DeviceManagementSection = () => {
  const [deviceId, setDeviceId] = useState('SL-001');
  const [newPin, setNewPin] = useState('');
  const [cardUid, setCardUid] = useState('');
  const [masterCardUid, setMasterCardUid] = useState('');
  const [masterCardName, setMasterCardName] = useState('');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });

  const showMessage = (type, text) => {
    setMessage({ type, text });
    setTimeout(() => setMessage({ type: '', text: '' }), 5000);
  };

  // Reset PIN
  const handleResetPin = async () => {
    if (newPin.length !== 6 || !/^\d+$/.test(newPin)) {
      showMessage('error', 'PIN must be exactly 6 digits');
      return;
    }

    try {
      setLoading(true);

      const { error } = await supabase
        .from('devices')
        .update({
          pending_command: {
            action: 'change_pin',
            new_pin: newPin,
            timestamp: new Date().toISOString(),
            source: 'web_app'
          }
        })
        .eq('device_id', deviceId);

      if (error) throw error;

      showMessage('success', 'PIN reset command sent to device. ESP32 will update PIN in 5-10 seconds.');
      setNewPin('');
    } catch (err) {
      showMessage('error', 'Failed to reset PIN: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  // Trigger Add Card Mode
  const handleAddCardMode = async () => {
    try {
      setLoading(true);

      const { error } = await supabase
        .from('devices')
        .update({
          pending_command: {
            action: 'add_card_mode',
            timestamp: new Date().toISOString(),
            source: 'web_app'
          }
        })
        .eq('device_id', deviceId);

      if (error) throw error;

      showMessage('success', 'Add card mode activated. Please scan card on device within 30 seconds.');
    } catch (err) {
      showMessage('error', 'Failed to activate add card mode: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  // Trigger Remove Card Mode
  const handleRemoveCardMode = async () => {
    try {
      setLoading(true);

      const { error } = await supabase
        .from('devices')
        .update({
          pending_command: {
            action: 'remove_card_mode',
            timestamp: new Date().toISOString(),
            source: 'web_app'
          }
        })
        .eq('device_id', deviceId);

      if (error) throw error;

      showMessage('success', 'Remove card mode activated. Please scan card on device within 30 seconds.');
    } catch (err) {
      showMessage('error', 'Failed to activate remove card mode: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  // Add Master Card
  const handleAddMasterCard = async () => {
    if (masterCardUid.length !== 8) {
      showMessage('error', 'Master card UID must be 8 characters');
      return;
    }

    try {
      setLoading(true);

      const { error } = await supabase
        .from('master_cards')
        .insert({
          card_uid: masterCardUid.toUpperCase(),
          card_name: masterCardName || 'New Master Card',
          is_active: true
        });

      if (error) throw error;

      showMessage('success', 'Master card added successfully!');
      setMasterCardUid('');
      setMasterCardName('');
    } catch (err) {
      showMessage('error', 'Failed to add master card: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  // Deactivate Master Card
  const handleDeactivateMasterCard = async () => {
    if (masterCardUid.length !== 8) {
      showMessage('error', 'Master card UID must be 8 characters');
      return;
    }

    try {
      setLoading(true);

      const { error } = await supabase
        .from('master_cards')
        .update({ is_active: false })
        .eq('card_uid', masterCardUid.toUpperCase());

      if (error) throw error;

      showMessage('success', 'Master card deactivated successfully!');
      setMasterCardUid('');
    } catch (err) {
      showMessage('error', 'Failed to deactivate master card: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Message Banner */}
      {message.text && (
        <div className={`p-4 rounded-lg border ${
          message.type === 'success' 
            ? 'bg-green-50 border-green-200 text-green-800' 
            : 'bg-red-50 border-red-200 text-red-800'
        }`}>
          <div className="flex items-center space-x-2">
            <Icon name={message.type === 'success' ? 'CheckCircle' : 'AlertCircle'} size={20} />
            <span className="text-sm font-medium">{message.text}</span>
          </div>
        </div>
      )}

      {/* Device Selection */}
      <div className="bg-card border border-border rounded-lg p-6">
        <h3 className="text-lg font-semibold mb-4 flex items-center">
          <Icon name="Smartphone" className="mr-2 text-primary" />
          Select Device
        </h3>
        <Input
          label="Device ID"
          value={deviceId}
          onChange={(e) => setDeviceId(e.target.value.toUpperCase())}
          placeholder="SL-001"
        />
      </div>

      {/* Reset PIN */}
      <div className="bg-card border border-border rounded-lg p-6">
        <h3 className="text-lg font-semibold mb-4 flex items-center">
          <Icon name="Key" className="mr-2 text-primary" />
          Reset Device PIN
        </h3>
        <div className="space-y-4">
          <Input
            label="New PIN (6 digits)"
            type="password"
            maxLength={6}
            value={newPin}
            onChange={(e) => setNewPin(e.target.value.replace(/\D/g, ''))}
            placeholder="123456"
          />
          <Button
            onClick={handleResetPin}
            loading={loading}
            disabled={newPin.length !== 6}
            iconName="Check"
            className="w-full"
          >
            Reset PIN
          </Button>
        </div>
      </div>

      {/* Manage Access Cards */}
      <div className="bg-card border border-border rounded-lg p-6">
        <h3 className="text-lg font-semibold mb-4 flex items-center">
          <Icon name="CreditCard" className="mr-2 text-primary" />
          Manage Access Cards
        </h3>
        <p className="text-sm text-muted-foreground mb-4">
          Click button below to activate add/remove mode, then scan card on device within 30 seconds
        </p>
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center space-y-3 sm:space-y-0 sm:space-x-3">
          <Button
            onClick={handleAddCardMode}
            loading={loading}
            iconName="Plus"
            variant="default"
            className="flex-1"
          >
            Activate Add Card Mode
          </Button>
          <Button
            onClick={handleRemoveCardMode}
            loading={loading}
            iconName="Trash"
            variant="destructive"
            className="flex-1"
          >
            Activate Remove Card Mode
          </Button>
        </div>
      </div>
  	  
  	  {/* Manage Master Cards */}
      <div className="bg-card border border-border rounded-lg p-6">
        <h3 className="text-lg font-semibold mb-4 flex items-center">
          <Icon name="Shield" className="mr-2 text-primary" />
          Manage Master Cards
        </h3>
        <div className="space-y-4">
          <Input
            label="Master Card UID (8 characters)"
            value={masterCardUid}
            onChange={(e) => setMasterCardUid(e.target.value.toUpperCase().slice(0, 8))}
            placeholder="73D48730"
            maxLength={8}
          />
          <Input
            label="Card Name (optional)"
            value={masterCardName}
            onChange={(e) => setMasterCardName(e.target.value)}
            placeholder="Admin Master Card"
          />
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center space-y-3 sm:space-y-0 sm:space-x-3">
            <Button
              onClick={handleAddMasterCard}
              loading={loading}
              disabled={masterCardUid.length !== 8}
              iconName="Plus"
              className="flex-1"
            >
              Add Master Card
            </Button>
            <Button
              onClick={handleDeactivateMasterCard}
              loading={loading}
              disabled={masterCardUid.length !== 8}
              iconName="X"
              variant="outline"
              className="flex-1"
            >
              Deactivate Master Card
            </Button>
          </div>
        </div>
      </div>

      {/* Clear EEPROM */}
      <div className="bg-card border border-border rounded-lg p-6 border-red-600/30">
        <h3 className="text-lg font-semibold mb-4 flex items-center text-red-600">
          <Icon name="Trash2" className="mr-2" />
          Danger Zone
        </h3>
        <p className="text-sm text-muted-foreground mb-4">
          Clear all registered cards from device memory. This action cannot be undone!
        </p>
        <Button
          onClick={async () => {
            if (!window.confirm('Are you sure you want to clear ALL cards from device memory? This cannot be undone!')) {
              return;
            }

            try {
              setLoading(true);

              const { error } = await supabase
                .from('devices')
                .update({
                  pending_command: {
                    action: 'clear_all_cards',
                    timestamp: new Date().toISOString(),
                    source: 'web_app'
                  }
                })
                .eq('device_id', deviceId);

              if (error) throw error;

              showMessage('success', 'Clear all cards command sent to device.');
            } catch (err) {
              showMessage('error', 'Failed to clear cards: ' + err.message);
            } finally {
              setLoading(false);
            }
          }}
          loading={loading}
          iconName="AlertTriangle"
          variant="destructive"
          className="w-full"
        >
          Clear All Cards from Device
        </Button>
      </div>

      {/* Info Box */}
      <div className="bg-muted border border-border rounded-lg p-4">
        <div className="flex items-start space-x-3">
          <Icon name="Info" className="text-accent mt-1" />
          <div className="text-sm text-muted-foreground">
            <p className="font-medium text-foreground mb-2">Important Notes:</p>
            <ul className="list-disc list-inside space-y-1">
              <li>PIN reset takes effect immediately on device (5-10 seconds)</li>
              <li>Card add/remove requires physical card scan within 30 seconds</li>
              <li>Multiple master cards can be active simultaneously</li>
              <li>Deactivating master card does not delete it from database</li>
              <li>ESP32 polls for commands every 5 seconds</li>
              <li>Check Telegram for confirmation messages</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DeviceManagementSection;